import clsx from "clsx";
import { format } from "date-fns";
import {
  createContext,
  FC,
  PropsWithChildren,
  CSSProperties,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { graphql, useFragment } from "react-relay";
import { Account, MsgExecute } from "rujira.js";
import {
  assetLabel,
  classAprValue,
  clipAmountString,
  Decimal,
  getDenomIconSrc,
  getDenomIconFallbackSrc,
  Icons,
  LoaderWithContent,
  Progress,
  SortDir,
  SortableTableHeader,
  TableFrame,
  TableColumn,
  TablePagination,
  TableTooltipLabel,
  toFiatDisplay,
  Toggle,
  TranslationProvider,
  compareBigInts,
  compareNullableBigInts,
  compareNullableDates,
  compareNullableNumbers,
  compareStrings,
  useGlobalModalContext,
  useIsTouchDevice,
  useLocale,
  useLocalStorage,
  useTablePagination,
  useTableSort,
  useTranslation,
} from "rujira.ui";
import { MsgProvider, TxButton } from "../../../common/components/TxButton";
import { useIsBelow } from "../../../hooks/useBreakpoint";
import { useHoverTip } from "../../../hooks/useHoverTip";
import { RangePositionShareButton } from "../../../sharing/RangePositionCard";
import { usePreloadedAccountData } from "../../../services/accountData";
import { useAccounts } from "../../../services/accounts";
import { assetToTradeUrlSegment } from "../../../services/assetUrl";
import { siteOrigin } from "../../../services/siteOrigin";
import { TradeBreakpoint } from "../../static";
import { cclCopyParams } from "../../tradeUrlState";
import { useTradeSubscriptionsFinRangeFragment } from "../../TradeSubscriptions";
import { useTradeContext } from "../Context";
import { OrderRows, useAlignedValues } from "../mobile/OrderRow";
import { RangeOrderRow } from "../mobile/RangeOrderRow";
import {
  RangeOrdersAccountFragment$data,
  RangeOrdersAccountFragment$key,
} from "./__generated__/RangeOrdersAccountFragment.graphql";
import { Apr, CurrentValue, Dpi, Moic, OrderLink } from "./Common";
import { useOrdersContext } from "./Context";
import { RangeManage } from "./RangeManage";
import { useRangeFormat } from "./shared/format";

// Prices carry 12 decimals throughout the app.
const PRICE_DECIMALS = 12;

enum RangeDetail {
  Hidden = "hidden",
  Visible = "visible",
}

type FinRangeNode = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<RangeOrdersAccountFragment$data["fin"]>["ranges"]
      >["edges"]
    >[number]
  >["node"]
>;

type RangeOrdersData = {
  open: FinRangeNode[];
  closed: FinRangeNode[];
  total: number;
};

const RANGE_SORT_KEYS = [
  "pair",
  "range",
  "created",
  "age",
  "totalInvested",
  "withdrawn",
  "claimedYield",
  "currentValue",
  "unclaimedYield",
  "moic",
  "dpi",
  "apr",
] as const;

const DETAILED_RANGE_SORT_KEYS: readonly RangeSortKey[] = [
  "totalInvested",
  "withdrawn",
  "claimedYield",
  "currentValue",
  "unclaimedYield",
];

type RangeSortKey = (typeof RANGE_SORT_KEYS)[number];

const rangePairLabel = (position: FinRangeNode) =>
  `${assetLabel(position.pair.assetBase)}/${assetLabel(position.pair.assetQuote)}`;

const quoteAmountUsd = (
  position: FinRangeNode,
  amount: bigint | null | undefined
): bigint | null => {
  const price = position.pair.assetQuote.price?.current;
  if (amount === null || amount === undefined || !price) return null;
  return (
    (amount * price) / 10n ** BigInt(position.pair.assetQuote.metadata.decimals)
  );
};

const compareRangeOrders = (
  a: FinRangeNode,
  b: FinRangeNode,
  sortBy: RangeSortKey,
  sortDir: SortDir
) => {
  const aAnalytics = a.analytics;
  const bAnalytics = b.analytics;
  switch (sortBy) {
    case "pair":
      return compareStrings(rangePairLabel(a), rangePairLabel(b), sortDir);
    case "range":
      return compareBigInts(a.high - a.low, b.high - b.low, sortDir);
    case "created":
      return compareNullableDates(
        aAnalytics?.firstDepositDate,
        bAnalytics?.firstDepositDate,
        sortDir
      );
    case "age":
      return compareNullableNumbers(
        aAnalytics?.weightedAverageInvestmentAgeDays,
        bAnalytics?.weightedAverageInvestmentAgeDays,
        sortDir
      );
    case "currentValue":
      return compareBigInts(a.principalUsd, b.principalUsd, sortDir);
    case "totalInvested":
      return compareNullableBigInts(
        quoteAmountUsd(a, aAnalytics?.totalInvested),
        quoteAmountUsd(b, bAnalytics?.totalInvested),
        sortDir
      );
    case "withdrawn":
      return compareNullableBigInts(
        quoteAmountUsd(a, aAnalytics?.totalWithdrawn),
        quoteAmountUsd(b, bAnalytics?.totalWithdrawn),
        sortDir
      );
    case "claimedYield":
      return compareNullableBigInts(
        quoteAmountUsd(a, aAnalytics?.realizedYield),
        quoteAmountUsd(b, bAnalytics?.realizedYield),
        sortDir
      );
    case "unclaimedYield":
      return compareBigInts(a.yieldUsd, b.yieldUsd, sortDir);
    case "moic":
      return compareNullableBigInts(
        aAnalytics?.moic,
        bAnalytics?.moic,
        sortDir
      );
    case "dpi":
      return compareNullableBigInts(aAnalytics?.dpi, bAnalytics?.dpi, sortDir);
    case "apr":
      return compareNullableBigInts(aAnalytics?.apr, bAnalytics?.apr, sortDir);
  }
};

type RangeColumn = TableColumn & {
  sortKey?: RangeSortKey;
  label?: ReactNode;
  align?: "left" | "center" | "right";
  className?: string;
};
type RangeColumnDefinition = RangeColumn & {
  detailedOnly?: boolean;
  trade?: Partial<TableColumn>;
  detailed?: Partial<TableColumn>;
};

const RANGE_PERFORMANCE_WIDTHS = {
  width: "6%",
  minWidth: "4.5rem",
  trade: { width: "5%", minWidth: "3rem" },
  detailed: { width: "4%", minWidth: "3.5rem" },
};

const getRangeColumns = (
  showDetails: boolean,
  tradeSimple = false
): RangeColumn[] => {
  const columns: RangeColumnDefinition[] = [
    { id: "logo", width: "2rem" },
    {
      id: "pair",
      width: "calc(13% - 2rem)",
      minWidth: "7rem",
      label: "Pair",
      sortKey: "pair",
      trade: { width: "6rem", minWidth: "6rem" },
      detailed: { width: "calc(11.5% - 2rem)", minWidth: "5rem" },
    },
    {
      id: "parameters",
      width: "9%",
      minWidth: "6rem",
      label: "Parameters",
      align: "center",
      trade: { minWidth: "5.5rem" },
      detailed: { width: "8.5%", minWidth: "5rem" },
    },
    {
      id: "range",
      width: "21%",
      minWidth: "10rem",
      label: "Range",
      sortKey: "range",
      trade: { width: "auto", minWidth: "7rem" },
      detailed: { width: "12%", minWidth: "8rem" },
    },
    {
      id: "created",
      width: "10%",
      minWidth: "6rem",
      label: "Created on",
      sortKey: "created",
      trade: { minWidth: "4.75rem" },
      detailed: { width: "6%", minWidth: "5.5rem" },
    },
    {
      id: "age",
      width: "9%",
      minWidth: "5rem",
      label: (
        <TableTooltipLabel
          label="Age"
          tooltip='<div class="w-30">Weighted average age of your position based on the size and timing of deposits and withdrawals. Used to calculate annualized returns like APR.<br/><br/>Example: a larger deposit made 30 days ago will have more impact on the calculation than a smaller deposit made yesterday.</div>'
        />
      ),
      sortKey: "age",
      align: "center",
      trade: { width: "7%", minWidth: "2.75rem" },
      detailed: { width: "4%", minWidth: "4.5rem" },
    },
    {
      id: "position-value",
      width: "11%",
      minWidth: "8rem",
      label: showDetails ? (
        <TableTooltipLabel
          label="Total Invested"
          tooltip='<div class="w-30">Total invested value measured in quote asset (e.g. USDC for BTC/USDC)</div>'
        />
      ) : (
        <CurrentValue />
      ),
      sortKey: showDetails ? "totalInvested" : "currentValue",
      align: "right",
      trade: { minWidth: "6.5rem" },
      detailed: { width: "8%", minWidth: "5.5rem" },
    },
    {
      id: "withdrawn",
      width: "7%",
      minWidth: "5rem",
      label: "Withdrawn",
      sortKey: "withdrawn",
      align: "left",
      className: "group-column-table__group-start bg-grey-1",
      detailedOnly: true,
    },
    {
      id: "claimed-yield",
      width: "7%",
      minWidth: "5rem",
      label: "Claimed Yield",
      sortKey: "claimedYield",
      align: "right",
      className: "group-column-table__group-end bg-grey-1",
      detailedOnly: true,
    },
    {
      id: "current-value",
      width: "8%",
      minWidth: "7rem",
      label: <CurrentValue />,
      sortKey: "currentValue",
      align: "left",
      detailedOnly: true,
    },
    {
      id: "unclaimed-yield",
      width: "9%",
      minWidth: "7rem",
      label: "Unclaimed Yield",
      sortKey: "unclaimedYield",
      align: "right",
      detailedOnly: true,
    },
    {
      id: "moic",
      ...RANGE_PERFORMANCE_WIDTHS,
      label: <Moic />,
      sortKey: "moic",
      align: "left",
      className: "group-column-table__group-start bg-grey-1",
    },
    {
      id: "dpi",
      ...RANGE_PERFORMANCE_WIDTHS,
      label: <Dpi />,
      sortKey: "dpi",
      align: "center",
      className: "bg-grey-1",
    },
    {
      id: "apr",
      ...RANGE_PERFORMANCE_WIDTHS,
      label: <Apr />,
      sortKey: "apr",
      align: "right",
      className: "group-column-table__group-end bg-grey-1",
    },
    {
      id: "actions",
      width: "9%",
      minWidth: "7rem",
      trade: { width: "10%", minWidth: "6rem" },
      detailed: { width: "7%" },
    },
  ];
  return columns
    .filter((column) => showDetails || !column.detailedOnly)
    .map(({ trade, detailed, detailedOnly: _detailedOnly, ...column }) => ({
      ...column,
      ...(showDetails ? detailed : tradeSimple ? trade : undefined),
    }));
};

const accountFragment = graphql`
  fragment RangeOrdersAccountFragment on Account {
    fin {
      ranges(first: 100) @connection(key: "TradeSubscriptionsFragment_ranges") {
        edges {
          node {
            id
            idx
            base
            quote
            feesBase
            feesQuote
            low
            high
            skew
            spread
            fee
            principalUsd
            yieldUsd
            inRange
            analytics {
              firstDepositDate
              weightedAverageInvestmentAgeDays
              totalInvested
              totalWithdrawn
              realizedYield
              unrealizedInvestment
              unrealizedYield
              moic
              dpi
              apr
            }
            ...RangeManageFragment
            ...TradeSubscriptionsFinRangeFragment
            analytics {
              unrealizedInvestment
            }
            pair {
              address
              book {
                center
              }
              assetBase {
                type
                chain
                asset
                metadata {
                  symbol
                  decimals
                }
                variants {
                  native {
                    denom
                  }
                }
                price {
                  current
                }
              }
              assetQuote {
                type
                chain
                asset
                metadata {
                  symbol
                  decimals
                }
                variants {
                  native {
                    denom
                  }
                }
                price {
                  current
                }
              }
            }
          }
        }
      }
    }
  }
`;

const context = createContext<{
  showDetails: boolean;
  setShowDetails: (v: boolean) => void;
}>({
  showDetails: false,
  setShowDetails: () => {},
});

const assetTooltipRow = (
  v: bigint,
  decimals: number,
  symbol: string,
  className?: string
): string => {
  return `<div class="flex ai-c${className ? ` ${className}` : ""}"><img src="${getDenomIconSrc(symbol)}" onerror="this.onerror=null;this.src='${getDenomIconFallbackSrc()}'" class="icon-denom w-2 mr-1" />${clipAmountString(v, decimals)} ${symbol}</div>`;
};

const assetPairTooltip = (
  base: { amount: bigint; decimals: number; symbol: string },
  quote: { amount: bigint; decimals: number; symbol: string },
  usd: bigint,
  title: string
): string => {
  return `<div>${title} (≈ $${toFiatDisplay(usd)})<div class="my-1">${assetTooltipRow(base.amount, base.decimals, base.symbol, "mb-1")}${assetTooltipRow(quote.amount, quote.decimals, quote.symbol)}</div></div>`;
};

export const Context: FC<PropsWithChildren> = ({ children }) => {
  const [detail, setDetail] = useLocalStorage<RangeDetail>(
    "orders-sub-range",
    RangeDetail.Hidden
  );
  const showDetails = detail === RangeDetail.Visible;

  return (
    <context.Provider
      value={{
        showDetails,
        setShowDetails: (v) =>
          setDetail(v ? RangeDetail.Visible : RangeDetail.Hidden),
      }}>
      {children}
    </context.Provider>
  );
};

export const useRangeOrdersData = (): RangeOrdersData => {
  const { accountData } = usePreloadedAccountData();
  const { only } = useOrdersContext();
  const { address } = useTradeContext();
  const d = useFragment<RangeOrdersAccountFragment$key>(
    accountFragment,
    accountData
  );

  return useMemo(() => {
    const rows =
      d?.fin?.ranges?.edges
        ?.map((x) => x?.node)
        .filter((node): node is FinRangeNode => !!node)
        .filter((node) =>
          address ? (only ? node.pair.address === address : true) : true
        ) || [];

    const open: FinRangeNode[] = [];
    const closed: FinRangeNode[] = [];
    for (const row of rows) {
      (row.base + row.quote + row.feesBase + row.feesQuote
        ? open
        : closed
      ).push(row);
    }
    return { open, closed, total: rows.length };
  }, [d, address, only]);
};

export const Tabs: FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { showDetails, setShowDetails } = useContext(context);

  return (
    <span className={clsx("flex ai-c as-c", !compact && "ml-2")}>
      <button
        type="button"
        className={`transparent fs-12 fs-sm-14 condensed fw-500 ${!showDetails ? "color-white" : "color-grey hover-white"} block mr-1 pointer`}
        onClick={() => setShowDetails(false)}>
        {compact ? "Simple" : "Simple View"}
      </button>
      <Toggle
        className={`toggle--xs ${showDetails ? "color-white" : "color-grey"}`}
        label={compact ? "Detailed" : "Detailed View"}
        checked={showDetails}
        onChange={(e) => setShowDetails(e.target.checked)}
      />
    </span>
  );
};

export const Content: FC<{ data: RangeOrdersData }> = ({ data: { open } }) => {
  const rows = useIsBelow(TradeBreakpoint);
  return <Table rows={rows} orders={open} layout="trade" />;
};

// these values are used to keep orders with mixed value sizes still looking
// aesthetic in terms of how we size and place the compact range indicator
const PIP_BASE_REM = 5;
const PIP_MIN_REM = 1.5;
const PIP_FREE_CHARS = 9;
const PIP_REM_PER_CHAR = 0.5;

const pipWidth = (orders: FinRangeNode[]) => {
  const longest = orders.reduce((max, p) => {
    const decimals = p.pair.assetQuote.metadata.decimals;
    // Negative values arrive as strings: relay's bigint transform only matches /^\d+$/
    const amount = BigInt(p.analytics?.unrealizedInvestment || 0n);
    return Math.max(max, clipAmountString(amount, decimals).length);
  }, 0);

  const over = Math.max(0, longest - PIP_FREE_CHARS);
  return `${Math.max(PIP_MIN_REM, PIP_BASE_REM - over * PIP_REM_PER_CHAR)}rem`;
};

export const Table: FC<{
  orders: FinRangeNode[];
  rows?: boolean;
  layout?: "portfolio" | "trade";
}> = ({ orders, rows, layout = "portfolio" }) => {
  const { t } = useTranslation();
  const isTouchDevice = useIsTouchDevice();
  const aside = useMemo(
    () => (rows ? pipWidth(orders) : undefined),
    [orders, rows]
  );
  const listRef = useAlignedValues(orders);
  const { showDetails } = useContext(context);
  const { sortBy, sortDir, toggleSort } = useTableSort<RangeSortKey>({
    initialBy: "pair",
    initialDir: SortDir.Asc,
    defaultDir: (key) => (key === "pair" ? SortDir.Asc : SortDir.Desc),
  });
  const columns = useMemo(
    () => getRangeColumns(showDetails, layout === "trade" && !showDetails),
    [showDetails, layout]
  );
  const effectiveSortBy =
    !showDetails && DETAILED_RANGE_SORT_KEYS.includes(sortBy)
      ? "currentValue"
      : sortBy;
  const sortedOrders = useMemo(
    () =>
      [...orders].sort((a, b) => {
        const comparison = compareRangeOrders(a, b, effectiveSortBy, sortDir);
        return comparison || (a.idx < b.idx ? -1 : a.idx > b.idx ? 1 : 0);
      }),
    [orders, effectiveSortBy, sortDir]
  );
  const { pageRows: visibleOrders, pagination } = useTablePagination(
    sortedOrders,
    { resetKey: `${showDetails}:${effectiveSortBy}:${sortDir}` }
  );

  if (orders.length === 0) {
    return (
      <p className="condensed fs-14 color-grey text-center mt-4">
        {t("noOrdersMessage", { label: t("automated") })}
      </p>
    );
  }

  if (rows) {
    return (
      <div
        ref={listRef}
        className="trade__orders-table mt-2"
        style={{ "--order-row-aside": aside } as CSSProperties}>
        <OrderRows>
          {orders.map((p) => (
            <Item key={p.id} item={p} rows />
          ))}
        </OrderRows>
      </div>
    );
  }

  return (
    <>
      <TableFrame
        className="trade__orders-table mt-2"
        columns={columns}
        stickyFirstColumn
        stickyLastColumn
        tableClassName={clsx({
          "table table--fixed nowrap table--spaced group-column-table": true,
          "table--no-hover": isTouchDevice,
        })}>
        <thead>
          <GroupHeaderRow />
          <HeaderRow
            columns={columns}
            active={effectiveSortBy}
            dir={sortDir}
            onSort={toggleSort}
          />
        </thead>
        <tbody>
          {visibleOrders.map((p) => (
            <Item key={p.id} item={p} />
          ))}
        </tbody>
      </TableFrame>
      <TablePagination {...pagination} />
    </>
  );
};

const GroupHeaderRow: FC = () => {
  const { showDetails } = useContext(context);
  return (
    <tr className="group-column-table__header-row">
      <th />
      <th />
      <th colSpan={5} />
      {showDetails && (
        <>
          <th colSpan={2} className="nopad">
            <div className="group-column-table__group-header bg-grey-1 text-center fs-12 color-grey fw-400">
              Realized Value
            </div>
          </th>
          <th colSpan={2} className="nopad">
            <div className="group-column-table__group-label text-center fs-14 color-grey fw-500">
              Unrealized Value
            </div>
          </th>
        </>
      )}
      <th colSpan={3} className="nopad">
        <div className="group-column-table__group-header bg-grey-1 text-center fs-12 color-grey fw-400">
          Performance
        </div>
      </th>
      <th />
    </tr>
  );
};

const HeaderRow: FC<{
  columns: readonly RangeColumn[];
  active: RangeSortKey;
  dir: SortDir;
  onSort: (key: RangeSortKey) => void;
}> = ({ columns, active, dir, onSort }) => (
  <tr className="group-column-table__header-row">
    {columns.map(({ id, sortKey, label, align, className }) =>
      sortKey ? (
        <SortableTableHeader
          key={id}
          active={active}
          dir={dir}
          onSort={onSort}
          value={sortKey}
          label={label}
          align={align}
          className={className}
        />
      ) : (
        <th
          key={id}
          scope="col"
          aria-hidden={id === "logo" ? true : undefined}
          className={align ? `text-${align}` : undefined}>
          {label}
        </th>
      )
    )}
  </tr>
);

export const InRange: FC<{
  current: bigint;
  low: bigint;
  high: bigint;
}> = ({ current, low, high }) => {
  if (!current && !low && !high) {
    return (
      <div className={"flex jc-c"}>
        <LoaderWithContent loading={true} content={""} />
      </div>
    );
  }

  const pct =
    current && high > low
      ? Math.max(
          0,
          Math.min(100, Number(((current - low) * 100n) / (high - low)))
        )
      : null;

  return (
    <div className="in-range">
      <div className="in-range__track">
        {pct !== null && (
          <>
            <span
              className="in-range__price color-teal fs-12"
              style={{ left: `${pct}%` }}>
              <Decimal
                as="span"
                amount={current}
                decimals={PRICE_DECIMALS}
                clip
              />
            </span>
            <div className="in-range-triangle" style={{ left: `${pct}%` }} />
          </>
        )}
        <Progress percentage={100} fillClassName="bg-teal" />
      </div>
      <div className="in-range__limits">
        <span className="in-range__limit-low color-teal fs-12">
          <Decimal as="span" amount={low} decimals={PRICE_DECIMALS} clip />
        </span>
        <span className="in-range__limit-high color-teal fs-12">
          <Decimal as="span" amount={high} decimals={PRICE_DECIMALS} clip />
        </span>
      </div>
    </div>
  );
};

export const OutOfRange: FC<{
  current: bigint;
  low: bigint;
  high: bigint;
}> = ({ current, low, high }) => {
  if (!current && !low && !high) {
    return (
      <div className={"flex jc-c"}>
        <LoaderWithContent loading={true} content={""} />
      </div>
    );
  }

  if (!current && low && high) {
    return <SimpleRange low={low} high={high} />;
  }

  if (!current) {
    return (
      <div className={"flex jc-c"}>
        <LoaderWithContent loading={true} content={""} />
      </div>
    );
  }

  const isBelow = current < low;
  const rangeToCurrent = isBelow ? high - current : current - low;
  const offset = isBelow
    ? Math.max(15, (Number(low - current) / Number(rangeToCurrent)) * 100)
    : 0;
  const pointerPos = isBelow ? 10 : 85;

  const pct = isBelow
    ? 100 - offset
    : Math.max(
        15,
        Math.min(
          85,
          ((Number(high) - Number(low)) / Number(rangeToCurrent)) * 100
        )
      );

  const pctToReenter = Number(
    ((isBelow ? low - current : current - high) * 100n) / current
  );

  const tooltipHtml = `Price needs to ${isBelow ? "rise" : "fall"} <span class="${isBelow ? "color-teal" : "color-red"}">${isBelow ? "+" : "-"}${pctToReenter.toFixed(2)}%</span> to re-enter the range`;

  return (
    <div
      className="out-of-range"
      data-tooltip-id="global-tip"
      data-tooltip-html={tooltipHtml}>
      <div className="out-of-range__track">
        <span
          className="out-of-range__price color-orange fs-12"
          style={{ left: `${pointerPos}%` }}>
          <Decimal as="span" amount={current} decimals={PRICE_DECIMALS} clip />
        </span>
        <div
          className="out-of-range-triangle"
          style={{ left: `${pointerPos}%` }}
        />
        <Progress
          percentage={pct}
          fillStart={offset}
          fillClassName="bg-orange"
        />
      </div>
      <div
        className="out-of-range__limits"
        style={{ justifyContent: isBelow ? "flex-end" : "flex-start" }}>
        <span className="color-orange fs-12 flex ai-c gap-0.5">
          <Decimal as="span" amount={low} decimals={PRICE_DECIMALS} clip />
          <span>—</span>
          <Decimal as="span" amount={high} decimals={PRICE_DECIMALS} clip />
        </span>
      </div>
    </div>
  );
};

export const SimpleRange: FC<{
  low: bigint;
  high: bigint;
}> = ({ low, high }) => {
  return (
    <div className="closed-range">
      <span className="color-white fs-12 nowrap">
        <Decimal as="span" amount={low} decimals={PRICE_DECIMALS} clip />
      </span>
      <span>-</span>
      <span className="color-white fs-12 nowrap text-right">
        <Decimal as="span" amount={high} decimals={PRICE_DECIMALS} clip />
      </span>
    </div>
  );
};

const Item: FC<{
  item: FinRangeNode;
  rows?: boolean;
}> = ({ item, rows }) => {
  const { t } = useTranslation();
  const tip = useHoverTip(TradeBreakpoint);
  const {
    notAvailable,
    formatAge,
    formatApr,
    formatPct,
    formatRatio,
    formatSkew,
  } = useRangeFormat();
  const { selected } = useAccounts();
  const { showDetails } = useContext(context);
  const { toRoot } = useLocale();
  const { hideModal, showModal } = useGlobalModalContext();
  const manage = useCallback(
    () =>
      showModal({
        children: (
          <TranslationProvider namespace="trade">
            <RangeManage k={item} hideModal={hideModal} />
          </TranslationProvider>
        ),
      }),
    []
  );

  const p = useTradeSubscriptionsFinRangeFragment(item);
  const base = p.pair.assetBase.metadata;
  const quote = p.pair.assetQuote.metadata;
  const baseLabel = assetLabel(p.pair.assetBase);
  const quoteLabel = assetLabel(p.pair.assetQuote);

  //this should come from analytics.status once it is working
  const closed = p.base + p.quote + p.feesBase + p.feesQuote === 0n;
  const centerPrice = p.pair.book?.center ?? 0n;

  const analytics = p.analytics;

  const copyUrl =
    siteOrigin() +
    toRoot(
      `/trade/${assetToTradeUrlSegment(p.pair.assetBase)}/${assetToTradeUrlSegment(
        p.pair.assetQuote
      )}${cclCopyParams(p)}`
    );

  const msg = useMemo(
    () =>
      selected && (p.feesBase || p.feesQuote)
        ? new MsgExecute(
            Account.fromAddress(selected.address),
            [],
            p.pair.address,
            { range: { claim: { idx: p.idx.toString() } } }
          )
        : null,
    [selected, p.feesBase, p.feesQuote, p.pair.address, p.idx]
  );

  const actions = !closed && (
    <>
      <button
        className="block transparent w-3 h-3 color-grey hover-white"
        onClick={manage}
        data-tooltip-content={t("manage")}
        data-tooltip-id="global-tip">
        <Icons.CirclePen className="block w-3 h-3" />
      </button>
      <RangePositionShareButton
        copyUrl={copyUrl}
        baseLabel={baseLabel}
        quoteLabel={quoteLabel}
        low={p.low}
        high={p.high}
        current={centerPrice}
        spread={p.spread}
        fee={p.fee}
        apr={analytics?.apr}
        ageDays={analytics?.weightedAverageInvestmentAgeDays}
        shareMsg={t("common:shareMsgOwnPosition", {
          base: baseLabel,
          quote: quoteLabel,
        })}
      />
      {msg ? (
        <span
          className="block ml-0.5"
          data-tooltip-html={
            tip &&
            assetPairTooltip(
              {
                amount: p.feesBase,
                decimals: base.decimals,
                symbol: baseLabel,
              },
              {
                amount: p.feesQuote,
                decimals: quote.decimals,
                symbol: quoteLabel,
              },
              p.yieldUsd,
              t("claimYield")
            )
          }
          data-tooltip-id={tip}
          data-tooltip-float={false}>
          <MsgProvider msg={msg}>
            <TxButton
              aria-label={t("claimYield")}
              className="block transparent w-3 h-3"
              hideSimulation>
              <Icons.ClaimSolid className="block w-3 h-3 color-primary1 hover-primary2" />
            </TxButton>
          </MsgProvider>
        </span>
      ) : (
        <button
          className="block transparent w-3 h-3 color-grey opacity-10 ml-0.5"
          data-tooltip-content={tip && t("claimYield")}
          data-tooltip-id={tip}
          data-tooltip-float={false}>
          <Icons.Claim className="block w-3 h-3" />
        </button>
      )}
    </>
  );

  if (rows) {
    return <RangeOrderRow p={p} showDetails={showDetails} actions={actions} />;
  }

  return (
    <tr key={p.id}>
      <OrderLink
        assetBase={p.pair.assetBase}
        assetQuote={p.pair.assetQuote}
        tooltip={`ID: #${p.idx}`}
      />
      <td className="w-a text-center">
        <TableTooltipLabel
          label="Details"
          tooltip={`<div>Spread: ${formatPct(p.spread)}<br/>Fee: ${formatPct(p.fee)}<br/>Skew: ${formatSkew(p.skew)}</div>`}
        />
      </td>
      <td>
        <div className="mr-1">
          {closed ? (
            <SimpleRange low={p.low} high={p.high} />
          ) : p.inRange ? (
            <InRange current={centerPrice} low={p.low} high={p.high} />
          ) : (
            <OutOfRange current={centerPrice} low={p.low} high={p.high} />
          )}
        </div>
      </td>
      <td>
        {analytics?.firstDepositDate
          ? format(analytics.firstDepositDate, "dd-MMM-yy")
          : notAvailable}
      </td>
      <td className="text-center">
        {formatAge(analytics?.weightedAverageInvestmentAgeDays)}
      </td>
      <td className="text-right">
        {showDetails ? (
          analytics?.totalInvested ? (
            <Decimal
              amount={analytics.totalInvested}
              decimals={quote.decimals}
              round={4}
              symbol={quote.symbol}
              symbolClassName="decimal__symbol--small color-grey condensed fw-500 mr-1"
              clip={true}
            />
          ) : (
            notAvailable
          )
        ) : (
          <span
            data-tooltip-id="global-tip"
            data-tooltip-html={assetPairTooltip(
              {
                amount: p.base,
                decimals: base.decimals,
                symbol: baseLabel,
              },
              {
                amount: p.quote,
                decimals: quote.decimals,
                symbol: quoteLabel,
              },
              p.principalUsd,
              "Current Value"
            )}>
            <Decimal
              amount={analytics?.unrealizedInvestment || 0n}
              decimals={quote.decimals}
              round={4}
              symbol={quote.symbol}
              symbolClassName="decimal__symbol--small color-grey condensed fw-500 mr-1"
              clip={true}
            />
          </span>
        )}
      </td>
      {showDetails && (
        <>
          <td className="group-column-table__group-start text-left bg-grey-1">
            <Decimal
              className={"ml-1"}
              amount={analytics?.totalWithdrawn || 0n}
              decimals={quote.decimals}
              round={4}
              symbol={quote.symbol}
              symbolClassName="decimal__symbol--small color-grey condensed fw-500"
              clip={true}
            />
          </td>
          <td className="group-column-table__group-end text-right bg-grey-1">
            <Decimal
              amount={analytics?.realizedYield || 0n}
              decimals={quote.decimals}
              round={4}
              symbol={quote.symbol}
              symbolClassName="decimal__symbol--small color-grey condensed fw-500 mr-1"
              clip={true}
            />
          </td>
          <td className="text-left">
            <span
              data-tooltip-id="global-tip"
              data-tooltip-html={assetPairTooltip(
                {
                  amount: p.base,
                  decimals: base.decimals,
                  symbol: baseLabel,
                },
                {
                  amount: p.quote,
                  decimals: quote.decimals,
                  symbol: quoteLabel,
                },
                p.principalUsd,
                "Current Value"
              )}>
              <Decimal
                className={"ml-1"}
                amount={analytics?.unrealizedInvestment || 0n}
                decimals={quote.decimals}
                round={4}
                symbol={quote.symbol}
                symbolClassName="decimal__symbol--small color-grey condensed fw-500 mr-1"
                clip={true}
              />
            </span>
          </td>
          <td className="ok I text-right">
            <span
              data-tooltip-id="global-tip"
              data-tooltip-html={assetPairTooltip(
                {
                  amount: p.feesBase,
                  decimals: base.decimals,
                  symbol: baseLabel,
                },
                {
                  amount: p.feesQuote,
                  decimals: quote.decimals,
                  symbol: quoteLabel,
                },
                p.yieldUsd,
                "Unclaimed Yield"
              )}>
              <Decimal
                amount={analytics?.unrealizedYield || 0n}
                decimals={quote.decimals}
                round={4}
                symbol={quote.symbol}
                symbolClassName="decimal__symbol--small color-grey condensed fw-500 mr-1"
                clip={true}
              />
            </span>
          </td>
        </>
      )}
      <td className="group-column-table__group-start text-left auto bg-grey-1">
        {formatRatio(analytics?.moic)}
      </td>
      <td className="text-center bg-grey-1">{formatRatio(analytics?.dpi)}</td>
      <td
        className={`group-column-table__group-end text-right bg-grey-1 ${
          p.fee === 0n ? "color-teal" : classAprValue(analytics?.apr)
        }`}>
        {p.fee === 0n ? (
          <span
            className="color-teal iflex ai-c"
            tabIndex={0}
            aria-label={t("common:compounding")}
            data-tooltip-content="This position compounds automatically, with rewards reinvested"
            data-tooltip-id="global-tip">
            <Icons.Seedling className="w-2 h-2" aria-hidden="true" />
          </span>
        ) : (
          formatApr(analytics?.apr)
        )}
      </td>
      <td className="text-right">
        <div className="flex jc-e trade__automated-actions">
          {closed ? (
            <>
              <button
                type="button"
                disabled
                aria-label={t("manage")}
                className="block transparent w-3 h-3 color-grey opacity-10">
                <Icons.CirclePen className="block w-3 h-3" />
              </button>
              <button
                type="button"
                disabled
                aria-label={t("common:share")}
                className="block transparent w-3 h-3 color-grey opacity-10 ml-0.5">
                <Icons.Share className="block w-3 h-3" />
              </button>
              <button
                type="button"
                disabled
                aria-label={t("claimYield")}
                className="block transparent w-3 h-3 color-grey opacity-10 ml-0.5">
                <Icons.Claim className="block w-3 h-3" />
              </button>
            </>
          ) : (
            <>
              <button
                className="block transparent w-3 h-3 color-grey hover-white"
                onClick={manage}
                data-tooltip-content={t("manage")}
                data-tooltip-id="global-tip">
                <Icons.CirclePen className="block w-3 h-3" />
              </button>
              <RangePositionShareButton
                copyUrl={copyUrl}
                baseLabel={baseLabel}
                quoteLabel={quoteLabel}
                low={p.low}
                high={p.high}
                current={centerPrice}
                spread={p.spread}
                fee={p.fee}
                apr={analytics?.apr}
                ageDays={analytics?.weightedAverageInvestmentAgeDays}
                shareMsg={t("common:shareMsgOwnPosition", {
                  base: baseLabel,
                  quote: quoteLabel,
                })}
              />
              {msg ? (
                <span
                  className="block ml-0.5"
                  data-tooltip-html={assetPairTooltip(
                    {
                      amount: p.feesBase,
                      decimals: base.decimals,
                      symbol: baseLabel,
                    },
                    {
                      amount: p.feesQuote,
                      decimals: quote.decimals,
                      symbol: quoteLabel,
                    },
                    p.yieldUsd,
                    t("claimYield")
                  )}
                  data-tooltip-id="global-tip"
                  data-tooltip-float={false}>
                  <MsgProvider msg={msg}>
                    <TxButton
                      aria-label={t("claimYield")}
                      className="block transparent w-3 h-3"
                      hideSimulation>
                      <Icons.ClaimSolid className="block w-3 h-3 color-primary1 hover-primary2" />
                    </TxButton>
                  </MsgProvider>
                </span>
              ) : (
                <button
                  type="button"
                  disabled
                  className="block transparent w-3 h-3 color-grey opacity-10 ml-0.5"
                  aria-label={t("claimYield")}>
                  <Icons.Claim className="block w-3 h-3" />
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
};
